import React, { useEffect, useMemo, useRef } from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  entryToItemListOption,
  MinimalScannerVariantPackListItem,
} from './minimal-scanner-variant-pack-list-item';
import { getVisibleQuantity } from '@/screens/new-order/hooks/use-quantity-reducer';
import {
  KeyboardAwareRecyclerList,
  RecyclerListRef,
} from '@/components/recycler-list';
import { ListOption } from '@/components/list-item';
import { ScannerType } from './scanner-types';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useItemQuantityState } from '@/stores/new-order-quantity-store';
import { useLocalScannerContext } from '@/contexts/local-scanner-context';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { VariantPackEntry } from '@/adapters/offline-mode';
import { View } from 'react-native';

type MinimalListOption = ListOption<VariantPackEntry>;

interface Props {
  scannerType: ScannerType;
}

// the estimate the recycler list uses before it measures real row heights;
// bump it when the additional info row adds a line to every row
const ESTIMATED_ITEM_SIZE = 80;
const ESTIMATED_ITEM_SIZE_WITH_ADDITIONAL_INFO = 110;

export const MinimalScanCartList: React.FC<Props> = ({ scannerType }) => {
  const [styles] = useStyles();
  const { selectItemListOption } = useItemDetailContext();
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const { cartEntries, cartMatchedCodes, removeFromOrder } =
    useLocalScannerContext();
  const listRef = useRef<RecyclerListRef<MinimalListOption> | null>(null);
  const additionalProductInfoEnabled = useSupplierDynamicConfigFlag(
    'on_device_scanning_config',
    'additional_product_info_enabled',
  );

  const cartEntryUUIDs = useMemo(
    () => (cartEntries ?? []).map((entry) => entry.uuid),
    [cartEntries],
  );
  const cartQuantityState = useItemQuantityState(cartEntryUUIDs);

  useEffect(() => {
    listRef.current?.scrollToTop();
  }, [cartEntries]);

  const sections = useMemo(() => {
    const data = (cartEntries ?? []).reduce<MinimalListOption[]>(
      (acc, entry) => {
        if (getVisibleQuantity(cartQuantityState, entry.uuid) > 0) {
          acc.push({
            metadata: entry,
            title: entry.display_name,
            value: entry.uuid,
          });
        }
        return acc;
      },
      [],
    );
    return [{ data, key: null }];
  }, [cartEntries, cartQuantityState]);

  return (
    <View style={styles.base}>
      <KeyboardAwareRecyclerList
        estimatedItemSize={
          additionalProductInfoEnabled
            ? ESTIMATED_ITEM_SIZE_WITH_ADDITIONAL_INFO
            : ESTIMATED_ITEM_SIZE
        }
        keyboardDismissMode="on-drag"
        keyboardShouldPersistTaps="handled"
        listItemProps={{
          listItemStyle: styles.listItem,
          onSelect: (item) =>
            selectItemListOption(entryToItemListOption(item.metadata!), {
              source: scannerType,
            }),
          renderMainContent: (option) => (
            <MinimalScannerVariantPackListItem
              additionalProductInfoEnabled={additionalProductInfoEnabled}
              debouncedUpdateQuantity={debouncedUpdateQuantity}
              entry={option.metadata!}
              matchedCode={cartMatchedCodes[option.metadata!.uuid]}
              onRemoveEntry={removeFromOrder}
              source={scannerType}
            />
          ),
        }}
        listRef={listRef}
        sections={sections}
        shouldRenderSeparator={() => false}
        showSeparator={false}
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
  },
  listItem: {
    backgroundColor: theme.colors.transparent,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
}));
